/**
 * multi.js
 * A user-friendly replacement for select boxes with multiple attribute enabled.
 *
 * Author: Fabian Lindfors
 * License: MIT
 */
export default class Multi {

    // Helper function to trigger an event on an element
    trigger_event(type, el) {
        var e = document.createEvent("HTMLEvents");
        e.initEvent(type, false, true);
        el.dispatchEvent(e);
    }

    // Toggles the target option on the select
    toggleOption(select, event) {
        var option = select.options[event.target.getAttribute("multi-index")];

        if (option.disabled) {
            return;
        }

        option.selected = !option.selected;
        this.trigger_event("change", select);
    }

    // Refreshes an already constructed multi.js instance
    refresh_select(select, settings) {
        // Clear columns
        select.wrapper.selected.innerHTML = "";
        select.wrapper.non_selected.innerHTML = "";

        // Get search value
        if (select.wrapper.search) {
            var query = select.wrapper.search.value;
        }

        // Current group
        var item_group = null;
        var current_optgroup = null;

        // Loop over select options and add to the non-selected and selected columns
        for (var i = 0; i < select.options.length; i++) {
            var option = select.options[i];

            var value = option.value;
            var label = option.textContent || option.innerText;

            var row = document.createElement("a");
            row.tabIndex = 0;
            row.className = "item";
            row.innerHTML = label;
            row.setAttribute("role", "button");
            row.setAttribute("data-value", value);
            row.setAttribute("multi-index", i);

            if (option.disabled) {
                row.className += " disabled";
            }

            // Add row to selected column if option selected
            if (option.selected) {
                row.className += " selected";
                var clone = row.cloneNode(true);
                select.wrapper.selected.appendChild(clone);
            }

            // Create group if entering a new optgroup
            if (option.parentNode.nodeName == "OPTGROUP" && option.parentNode != current_optgroup) {
                current_optgroup = option.parentNode
                item_group = document.createElement("div");
                item_group.className = "item-group";

                if (option.parentNode.label) {
                    var label = document.createElement("span");
                    label.innerHTML = option.parentNode.label;
                    label.className = "group-label"
                    item_group.appendChild(label);
                }

                select.wrapper.non_selected.appendChild(item_group);
            }

            // Clear group if not inside optgroup
            if (option.parentNode == select) {
                item_group = null;
                current_optgroup = null;
            }

            // Apply search filtering
            if (!query || query && label.toLowerCase().indexOf(query.toLowerCase()) > -1) {
                // Append to group if one exists, else just append to wrapper
                if (item_group != null) {
                    item_group.appendChild(row);
                } else {
                    select.wrapper.non_selected.appendChild(row);
                }
            }
        }

        // Put an inner wrapper around the non selected and selected wrapper
        let inner = document.createElement("div");
        inner.className = "inner";
        let inner2 = inner.cloneNode();

        this.wrapContentOfElementWith(select.wrapper.non_selected, inner);
        this.wrapContentOfElementWith(select.wrapper.selected, inner2);

        // Add headers to columns
        if (settings.non_selected_header && settings.selected_header) {
            var non_selected_header = document.createElement("div");
            var selected_header = document.createElement("div");

            non_selected_header.className = "header";
            selected_header.className = "header";

            non_selected_header.innerText = settings.non_selected_header;
            selected_header.innerText = settings.selected_header;

            // If we have search enabled, add the search input to the non-selected list
            if(select.wrapper.search) {
                let div = document.createElement("div");
                div.className = "search-holder"
                div.appendChild(select.wrapper.search)
                select.wrapper.non_selected.prepend(div);
            }

            select.wrapper.non_selected.prepend(non_selected_header);
            select.wrapper.selected.prepend(selected_header);
        }

        // Put an inner wrapper around the non selected and selected wrapper
        let columnWrapper = document.createElement("div");
        columnWrapper.className = "col-md-6";
        let columnWrapper2 = columnWrapper.cloneNode();

        columnWrapper.appendChild(select.wrapper.non_selected);
        columnWrapper2.appendChild(select.wrapper.selected);

        select.wrapper.innerHTML = "";
        select.wrapper.appendChild(columnWrapper);
        select.wrapper.appendChild(columnWrapper2);

        if(select.wrapper.search) {
            select.wrapper.search.focus(); // Focus cursor in search field
        }
    }

    wrapContentOfElementWith(element, wrapper) {
        while(element.firstChild) {
            wrapper.appendChild(element.firstChild);
        }
        element.appendChild(wrapper);
    }

    triggerRefresh() {
        this.refresh_select(this.select, this.settings)
    }

    // Intializes and constructs an multi.js instance
    constructor(select, settings) {

        /**
         * Set up settings (optional parameter) and its default values
         *
         * Default values:
         * enable_search : true
         * search_placeholder : "Search..."
         */
        settings = typeof settings !== "undefined" ? settings : {};

        settings["enable_search"] = typeof settings["enable_search"] !== "undefined" ? settings["enable_search"] : true;
        settings["search_placeholder"] = typeof settings["search_placeholder"] !== "undefined" ? settings["search_placeholder"] : "Search...";
        settings["non_selected_header"] = typeof settings["non_selected_header"] !== "undefined" ? settings["non_selected_header"] : null;
        settings["selected_header"] = typeof settings["selected_header"] !== "undefined" ? settings["selected_header"] : null;


        // Check if already initalized
        if (select.dataset.multijs != null) {
            return;
        }

        // Make sure element is select and multiple is enabled
        if (select.nodeName != "SELECT" || !select.multiple) {
            return;
        }

        // Hide select
        select.style.display = "none";
        select.setAttribute("data-multijs", true);

        // Start constructing selector
        var wrapper = document.createElement("div");
        wrapper.className = "multi-wrapper row";

        // Add search bar
        if (settings.enable_search) {
            var search = document.createElement("input");
            search.className = "search-input";
            search.type = "text";
            search.setAttribute("placeholder", settings.search_placeholder);

            search.addEventListener("input", () => {
                this.refresh_select(select, settings);
            });

            wrapper.appendChild(search);
            wrapper.search = search;
        }


        // Add columns for selected and non-selected
        var non_selected = document.createElement("div");
        non_selected.className = "non-selected-wrapper";

        var selected = document.createElement("div");
        selected.className = "selected-wrapper";

        // Add click handler to toggle the selected status
        wrapper.addEventListener("click", (event) => {
            if (event.target.getAttribute("multi-index")) {
                this.toggleOption(select, event);
            }
        })


        // Add keyboard handler to toggle the selected status
        wrapper.addEventListener("keypress", (event) => {
            var is_action_key = event.keyCode === 32 || event.keyCode === 13;
            var is_option = event.target.getAttribute("multi-index");

            if (is_option && is_action_key) {
                // Prevent the default action to stop scrolling when space is pressed
                event.preventDefault();
                this.toggleOption(select, event);
            }
        })


        wrapper.appendChild(non_selected);
        wrapper.appendChild(selected);

        wrapper.non_selected = non_selected;
        wrapper.selected = selected;

        select.wrapper = wrapper;

        // Add multi.js wrapper after select element
        select.parentNode.insertBefore(wrapper, select.nextSibling);
        
        // Initialize selector with values from select element
        this.refresh_select(select, settings);

        this.select = select
        this.settings = settings

        // Refresh selector when select values change
        select.addEventListener("change", () => {
            this.refresh_select(select, settings)
        })
    }
}